// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-floating-promises, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, no-case-declarations, react-hooks/exhaustive-deps, react-hooks/immutability */

import * as turf from "@turf/turf";
import Tooltip from "rc-tooltip";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Icon } from "semantic-ui-react";
import { useDraw, useDrawDispatch } from "../StateManagement";
import { DraggableWindow } from "./InternalComponents/DraggableWindow";

export const MeasureTool = () => {
	const [popupOpen, setPopupOpen] = useState(false);

	return (
		<>
			<Button
				icon
				compact
				onClick={() => {
					setPopupOpen((prev) => !prev);
				}}
				toggle
				active={popupOpen}
			>
				<Icon className="ruler" size="large" />
				<span>Measurement Tools</span>
			</Button>
			{createPortal(<MeasureToolPopup open={popupOpen} setOpen={setPopupOpen} />, document.body)}
		</>
	);
};

type PopupProps = {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const MeasureToolPopup = ({ open, setOpen }: PopupProps) => {
	const [currentTool, setCurrentTool] = useState("Measurement Tools");
	const [measuredInfo, setMeasuredInfo] = useState("");
	const [coords, setCoords] = useState<any[] | null>(null);
	const [infoCopied, setInfoCopied] = useState(false);
	const { drawnFeature } = useDraw();
	const { drawRef } = useDrawDispatch();

	useEffect(() => {
		if (open) {
			setCurrentTool("Line Measure");
		} else {
			setCurrentTool("Measurement Tools");
		}
	}, [open]);

	useEffect(() => {
		if (drawRef.current) {
			const draw = drawRef.current;
			switch (currentTool) {
				case "Line Measure":
					draw.deleteAll();
					draw.changeMode("line_measure", {});
					break;
				case "Polygon Measure":
					draw.deleteAll();
					draw.changeMode("polygon_measure");
					break;
				case "Coordinate at Point":
					draw.deleteAll();
					draw.changeMode("point");
					break;
				default:
					draw.deleteAll();
					draw.changeMode("simple_select");
					break;
			}
		}
	}, [currentTool]);

	useEffect(() => {
		if (drawnFeature) {
			let coords = [];
			switch (currentTool) {
				case "Line Measure":
					const lineDistance = Math.round(turf.length(drawnFeature, { units: "feet" }) * 100) / 100;
					coords = getMeasuredCoords(drawnFeature);
					setMeasuredInfo(`${lineDistance.toLocaleString()} ft`);
					setCoords(coords);
					break;
				case "Polygon Measure":
					const area = Math.round((turf.area(drawnFeature) * 100) / 4047) / 100;
					coords = getMeasuredCoords(drawnFeature);
					setMeasuredInfo(`${area.toLocaleString()} ac`);
					setCoords(coords);
					break;
				case "Coordinate at Point":
					setMeasuredInfo("");
					coords = getMeasuredCoords(drawnFeature);
					setCoords(coords);
					break;
				default:
					setMeasuredInfo("");
					setCoords(null);
					break;
			}
		}
	}, [drawnFeature, currentTool]);

	const getMeasuredCoords = (feature: GeoJSON.Feature) => {
		let geometry;

		if (feature.geometry.type === "Polygon") {
			geometry = feature.geometry.coordinates[0];
			geometry = geometry.slice(0, -1);
		} else if (feature.geometry.type === "LineString") {
			geometry = feature.geometry.coordinates;
		} else if (feature.geometry.type === "Point") {
			geometry = [feature.geometry.coordinates];
		}

		// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
		const coords = geometry.map((c) => [Math.round(c[1] * 1000000) / 1000000, Math.round(c[0] * 1000000) / 1000000]);
		return coords;
	};

	return open ? (
		<DraggableWindow
			zId="measure"
			title={currentTool}
			open={open}
			setOpen={setOpen}
			contentStyle={{ justifyContent: "start" }}
		>
			<Button.Group>
				<Button
					icon
					compact
					onClick={() => setCurrentTool("Line Measure")}
					toggle
					active={currentTool === "Line Measure"}
				>
					<Icon className="line measure" size="large" />
					<span>Line</span>
				</Button>
				{/* <Button
								icon
								compact
								onClick={measureBox}
								toggle
								active={currentTool === "Rectangle Measure"}
								disabled={disabled}
							>
								<Icon name="square outline" size="large" />
								<span>Square</span>
							</Button> */}
				<Button
					icon
					compact
					onClick={() => setCurrentTool("Polygon Measure")}
					toggle
					active={currentTool === "Polygon Measure"}
				>
					<Icon className="polygon" size="large" />
					<span>Polygon</span>
				</Button>
				<Button
					icon
					compact
					onClick={() => setCurrentTool("Coordinate at Point")}
					toggle
					active={currentTool === "Coordinate at Point"}
				>
					<Icon className="point" size="large" />
					<span>Coordinate</span>
				</Button>

				<Button icon compact onClick={() => setCurrentTool("Measurement Tools")}>
					<Icon className="trash" size="large" />
					<span>Clear</span>
				</Button>
			</Button.Group>

			<div style={{ cursor: "pointer", paddingTop: 10 }}>
				{(measuredInfo || coords) && (
					<>
						{measuredInfo && (
							<Tooltip
								placement="top"
								align={{ offset: [0, 2] }}
								mouseEnterDelay={0}
								mouseLeaveDelay={0}
								trigger="hover"
								overlay={!infoCopied ? "Copy Measurements" : "Measurements Copied"}
							>
								<p
									onClick={() => {
										setInfoCopied(true);
										navigator.clipboard.writeText(measuredInfo);
									}}
									onMouseLeave={() => {
										setInfoCopied(false);
									}}
								>
									Measured Info: {measuredInfo}
								</p>
							</Tooltip>
						)}
						{coords && (
							<Tooltip
								placement="top"
								align={{ offset: [0, 2] }}
								mouseEnterDelay={0}
								mouseLeaveDelay={0}
								trigger="hover"
								overlay={!infoCopied ? "Copy Coordinates" : "Coordinates Copied"}
							>
								<p
									onClick={() => {
										setInfoCopied(true);
										navigator.clipboard.writeText(
											coords.reduce((acc, cur) => acc + `${cur[0]},${cur[1]},\n`, "").slice(0, -1),
										);
									}}
									onMouseLeave={() => {
										setInfoCopied(false);
									}}
								>
									Coords:{" "}
									{coords.map((c, i) => (
										<p key={`${c[0]},${c[1]}`} style={{ margin: 0 }}>
											{i + 1}: ({c[0]}, {c[1]})
										</p>
									))}
								</p>
							</Tooltip>
						)}
					</>
				)}
			</div>
		</DraggableWindow>
	) : (
		<></>
	);
};
